import HpFooter from "../HP/HpFooter";
import "../ScrollAnimations.css";
import ImageContainer from "../components/ImageContainer";
import StructuredData from "../components/StructuredData";
import CompareWebsiteClosing from "../components/CompareWebsiteClosing";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { isMacOS, isSafari } from "../player/Utils";

// Fonction pour détecter la langue du navigateur
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0].toLowerCase();
  return langCode === 'fr' ? 'fr' : 'en';
};

const CALENDLY_DEMO_URL = "https://calendly.com/jacques-upreality/30min";

function openCalendlyDemo(analyticsManager, origin) {
  analyticsManager.logHPRequestDemoTag(origin);
  window.open(CALENDLY_DEMO_URL, "_blank", "noopener,noreferrer");
}

const INSTANT_AD_BG_GREY = "#f7f9fc";
const INSTANT_AD_BG_WHITE = "#ffffff";
const INSTANT_AD_BRAND_GREEN = "#2fc4b5";
const INSTANT_AD_BRAND_GREEN_SOFT = "rgba(4, 211, 188, 0.12)";
/** Essai : même vert que « Créer mon compte » (#2fc4b5) pour landing page / CTAs / pastilles */
const HP_HERO_TEAL = INSTANT_AD_BRAND_GREEN;
/** Même teinte / fill que « Créer mon compte » (topbar-btn-primary). */
const HP_LANDING_CTA_SX = {
  color: "#ffffff",
  backgroundColor: HP_HERO_TEAL,
  borderColor: HP_HERO_TEAL,
  boxShadow: "none",
  "&:hover": {
    color: "#ffffff",
    backgroundColor: "#25b7a8",
    borderColor: "#25b7a8",
    boxShadow: "none",
  },
};

// Fond hero (Revamping UI)
const HP_SLIDE_BG_HERO = INSTANT_AD_BG_WHITE;
// Fond mint zip (témoignages / Pour qui / FAQ)
const HP_SLIDE_BG_WHO_FOR = "#e7f6f1";

const HP_HERO_TITLE_STYLE = {
  margin: 0,
  fontWeight: 700,
  letterSpacing: "-0.045em",
  lineHeight: 1.02,
  color: "rgb(45, 55, 72)",
};

/** Titre hero Revamping UI : « landing page » en vert, un cran plus petit que le mock. */
function renderHpHeroTitle(t, { compact = false, as: Tag = "h1" } = {}) {
  return (
    <Tag
      style={{
        ...HP_HERO_TITLE_STYLE,
        fontSize: compact ? "clamp(34px, 8.5vw, 44px)" : "clamp(40px, 4.2vw, 58px)",
      }}
    >
      {t("InstantAdHero1")}{" "}
      <span style={{ color: HP_HERO_TEAL }}>{t("InstantAdHero2")}</span>
      <br />
      {t("InstantAdHero3")}
    </Tag>
  );
}

/** Titre diapo planche — 2 lignes max (nowrap sur chaque ligne). */
function renderHpPlancheTitle(t, { compact = false, centered = false, as: Tag = "h2" } = {}) {
  const text = t("RestaurantsBenefit1");
  // Coupure naturelle type zip : « …découverte » / « de votre restaurant »
  const breakMatch = text.match(/^(.*?\b(?:découverte|discovery board))\s+(.*)$/i);
  const lineStyle = { display: "block", whiteSpace: "nowrap" };
  return (
    <Tag
      style={{
        ...HP_HERO_TITLE_STYLE,
        // Compact : légèrement réduit pour tenir en 2 lignes
        fontSize: compact ? "clamp(34px, 3.9vw, 46px)" : "clamp(40px, 4.2vw, 58px)",
        textAlign: centered ? "center" : "left",
        maxWidth: "100%",
      }}
    >
      {breakMatch ? (
        <>
          <span style={lineStyle}>{breakMatch[1]}</span>
          <span style={lineStyle}>{breakMatch[2]}</span>
        </>
      ) : (
        <span style={lineStyle}>{text}</span>
      )}
    </Tag>
  );
}

/** Vagues latérales panorama : path max 183 / viewBox 1440, scale 0.55 → ~7 % de largeur. */
const HP_PANORAMA_SIDE_SCALE = 0.55;
const HP_PANORAMA_SIDE_PATH_W = 183;
const HP_PANORAMA_VIEWBOX_W = 1440;
const HP_PANORAMA_SIDE_INSET = `max(16px, calc(100% * ${HP_PANORAMA_SIDE_PATH_W * HP_PANORAMA_SIDE_SCALE} / ${HP_PANORAMA_VIEWBOX_W}))`;

/** Fond diapo planche — zip Revamping UI option 01 Panorama (wave-frame--full). */
function renderHpDiscoveryPanoramaWaves() {
  const topPaths = (
    <>
      <path d="M0 0h1440v172c-176-86-298 23-468-19-166-41-289-103-465-25C329 207 177 101 0 168Z" fill="#dff4ed" />
      <path d="M0 0h1440v132c-173-79-296 41-466-3-168-44-290-92-468-29C328 163 172 66 0 137Z" fill="#8bd8ce" />
      <path d="M0 0h1440v91c-166-66-288 48-458 7-172-41-298-82-471-25C328 133 169 39 0 101Z" fill="#35aaa7" />
    </>
  );
  const sidePaths = (
    <>
      <path d="M0 0h183c-78 121 31 213-16 338-48 128-96 219-25 350 40 73 37 143-8 212H0Z" fill="#dff4ed" />
      <path d="M0 0h135c-61 124 40 212-2 338-43 128-84 222-24 349 36 76 30 146-15 213H0Z" fill="#8bd8ce" />
      <path d="M0 0h91c-44 121 39 211 4 335-37 132-69 227-18 352 31 77 23 148-18 213H0Z" fill="#35aaa7" />
    </>
  );
  return (
    <svg
      className="hp-discovery-panorama-waves"
      viewBox={`0 0 ${HP_PANORAMA_VIEWBOX_W} 900`}
      preserveAspectRatio="none"
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      <g transform={`scale(1 ${HP_PANORAMA_SIDE_SCALE})`}>{topPaths}</g>
      <g transform={`translate(${HP_PANORAMA_VIEWBOX_W} 900) rotate(180) scale(1 ${HP_PANORAMA_SIDE_SCALE})`}>{topPaths}</g>
      <g transform={`scale(${HP_PANORAMA_SIDE_SCALE} 1)`}>{sidePaths}</g>
      <g transform={`translate(${HP_PANORAMA_VIEWBOX_W} 0) scale(-${HP_PANORAMA_SIDE_SCALE} 1)`}>{sidePaths}</g>
    </svg>
  );
}

/** Vague bas de diapo 1 (Revamping UI). */
function renderHpHeroWaves({ height = 120 } = {}) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 1440 320"
      preserveAspectRatio="none"
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height,
        zIndex: 1,
        pointerEvents: "none",
        display: "block",
      }}
    >
      <path d="M0 80c192-68 358-43 529 29 201 84 355 65 527-21C1194 19 1322 23 1440 76v244H0Z" fill="#dff4ed" />
      <path d="M0 132c190-59 355-37 532 29 194 73 353 45 526-28 143-60 263-46 382 3v184H0Z" fill="#bfeadc" />
      <path d="M0 187c181-50 347-34 530 19 197 57 362 22 533-37 141-49 259-27 377 16v135H0Z" fill="#83d6cc" />
      <path d="M0 245c188-37 357-22 540 14 193 38 358-1 527-39 137-31 255-7 373 26v74H0Z" fill="#32b9b5" />
    </svg>
  );
}

const instantAdSlideTitleStyle = {
  marginTop: 0,
  marginBottom: 48,
  fontSize: 30,
  fontWeight: 700,
  color: "rgb(45, 55, 72)",
  textAlign: "center",
  lineHeight: 1.2,
};

const instantAdSlideTitleStyleCompact = {
  ...instantAdSlideTitleStyle,
  marginBottom: 32,
  fontSize: 24,
};

function instantAdSlideShellStyle(background, { compact = false, fullViewport = false } = {}) {
  const isGradient = typeof background === "string" && background.includes("gradient");
  return {
    marginTop: 0,
    width: "100%",
    ...(isGradient ? { background } : { backgroundColor: background }),
    padding: compact ? "28px 16px 40px" : "80px 24px 80px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    ...(fullViewport && !compact ? { minHeight: window.innerHeight, justifyContent: "center" } : {}),
  };
}

const HP_DESKTOP_VISUAL_COMPACT_SCALE = 0.9;
const HP_DESKTOP_VISUAL_COMPACT_MAX_HEIGHT = 880;

function scaleHpDesktopVisual(value, scale = 1) {
  return Math.round(value * scale);
}

/** Réf. coque HP (comme UseCasesDemo / DemoFeed : on scale ensuite selon le viewport). */
const HP_PHONE_REF = {
  deviceBgHeight: 576,
  storyHeight: 540,
  storyTopMargin: 16,
  frameWidth: 274,
  playerWidth: 244,
  frameMarginLeft: 14,
  heroArrowWidth: 72,
  heroCalloutOffset: 250,
  heroCalloutMaxWidth: 160,
  createSlideImageHeight: 450,
  createSlideVideoWidth: 455,
};

function getHpDesktopVisualMetrics(deskTop, { compactLaptop = false } = {}) {
  const scale = compactLaptop ? HP_DESKTOP_VISUAL_COMPACT_SCALE : 1;
  const s = (value) => scaleHpDesktopVisual(value, scale);
  // compactLaptop : toujours partir des métriques « grand téléphone » avant le scale (évite un 2e palier < 688 px)
  const useLargePhoneMetrics = deskTop || compactLaptop;
  const deviceBgHeight = useLargePhoneMetrics ? s(HP_PHONE_REF.deviceBgHeight) : s(516);
  const storyHeight = useLargePhoneMetrics ? s(HP_PHONE_REF.storyHeight) : s(486);
  const storyTopMargin = useLargePhoneMetrics ? s(HP_PHONE_REF.storyTopMargin) : s(14);
  const frameWidth = s(HP_PHONE_REF.frameWidth);
  const playerWidth = s(HP_PHONE_REF.playerWidth);
  const frameMarginLeft = s(HP_PHONE_REF.frameMarginLeft);
  return {
    scale,
    deviceBgHeight,
    storyHeight,
    storyTopMargin,
    frameWidth,
    playerWidth,
    frameMarginLeft,
    heroArrowWidth: s(HP_PHONE_REF.heroArrowWidth),
    heroCalloutMarginTop: storyTopMargin + s(HP_PHONE_REF.heroCalloutOffset),
    heroCalloutMaxWidth: s(HP_PHONE_REF.heroCalloutMaxWidth),
    createSlideImageHeight: s(HP_PHONE_REF.createSlideImageHeight),
    createSlideVideoWidth: s(HP_PHONE_REF.createSlideVideoWidth),
  };
}

/**
 * Diapo planche : hauteur téléphone adaptée à l’espace dispo (layout titre | coque).
 */
function getHpPlancheFitVisualMetrics({ viewportHeight, compactLaptop = false }) {
  const padTop = compactLaptop ? 48 : Math.round(viewportHeight * 0.1);
  const padBottom = compactLaptop ? 36 : 48;
  const availableH = Math.max(280, viewportHeight - padTop - padBottom);
  const maxScale = compactLaptop ? 1.0 : 1.12;
  const minScale = compactLaptop ? 0.55 : 0.58;
  let scale = Math.min(availableH / HP_PHONE_REF.deviceBgHeight, maxScale);
  scale = Math.max(minScale, scale);
  const s = (value) => Math.round(value * scale);
  return {
    scale,
    deviceBgHeight: s(HP_PHONE_REF.deviceBgHeight),
    storyHeight: s(HP_PHONE_REF.storyHeight),
    storyTopMargin: s(HP_PHONE_REF.storyTopMargin),
    frameWidth: s(HP_PHONE_REF.frameWidth),
    playerWidth: s(HP_PHONE_REF.playerWidth),
    frameMarginLeft: s(HP_PHONE_REF.frameMarginLeft),
    padTop,
    padBottom,
  };
}

/**
 * Diapo 1 HP : scale les coques pour tenir en 1 écran (hauteur + largeur).
 * Si place manque : d’abord gap texte↔coques, puis largeur texte, puis coques.
 */
function getHpHeroFitVisualMetrics({
  viewportHeight,
  viewportWidth,
  topbarHeight,
  compactLaptop = false,
}) {
  const waveReserve = compactLaptop ? 56 : 72;
  const padY = compactLaptop ? 4 : 8;
  const availablePhoneH = Math.max(260, viewportHeight - topbarHeight - waveReserve - padY * 2);

  const heroLeftInset = 12;
  const heroTextWidthPreferred = compactLaptop ? 420 : 540;
  const heroTextWidthMin = 300;
  const gapPreferred = compactLaptop ? 24 : 56;
  const gapMin = 4;
  const mediaGapPreferred = 12;
  const mediaGapMin = 4;
  // Compact : coques un cran plus petites que le fit max
  const maxPhoneScale = compactLaptop ? 0.96 : 1.12;
  const minPhoneScale = compactLaptop ? 0.58 : 0.64;

  let heroTextWidth = heroTextWidthPreferred;
  let heroTextToVisualGap = gapPreferred;
  let heroMediaGap = mediaGapPreferred;
  let phoneScale = Math.min(availablePhoneH / HP_PHONE_REF.deviceBgHeight, maxPhoneScale);

  const rowWidth = (scale, gap, textW, mediaGap) => {
    const deviceH = HP_PHONE_REF.deviceBgHeight * scale;
    const frameW = HP_PHONE_REF.frameWidth * scale;
    const calloutW = HP_PHONE_REF.heroCalloutMaxWidth * scale;
    const igW = deviceH * 0.48;
    return (
      heroLeftInset
      + textW
      + gap
      + igW
      + mediaGap
      + frameW
      + mediaGap
      + calloutW
      + 20
    );
  };

  for (let i = 0; i < 10; i += 1) {
    const needed = rowWidth(phoneScale, heroTextToVisualGap, heroTextWidth, heroMediaGap);
    if (needed <= viewportWidth) break;
    const overflow = needed - viewportWidth;
    if (heroTextToVisualGap > gapMin) {
      heroTextToVisualGap -= Math.min(heroTextToVisualGap - gapMin, overflow);
      continue;
    }
    if (heroMediaGap > mediaGapMin) {
      heroMediaGap -= Math.min(heroMediaGap - mediaGapMin, Math.ceil(overflow / 2));
      continue;
    }
    if (heroTextWidth > heroTextWidthMin) {
      heroTextWidth -= Math.min(heroTextWidth - heroTextWidthMin, overflow);
      continue;
    }
    phoneScale = Math.max(minPhoneScale, phoneScale * (viewportWidth / needed) * 0.97);
  }

  phoneScale = Math.min(phoneScale, availablePhoneH / HP_PHONE_REF.deviceBgHeight);
  phoneScale = Math.max(minPhoneScale, Math.min(phoneScale, maxPhoneScale));

  const tight = heroTextToVisualGap <= 16 || phoneScale < 0.92 || heroTextWidth < heroTextWidthPreferred - 40;
  // Bloc titre + CTAs plus bas que les coques (compact un cran plus bas)
  const heroTextMarginTop = Math.round(
    (tight ? (compactLaptop ? 84 : 76) : (compactLaptop ? 104 : 120)) * Math.min(1, phoneScale)
  );

  const s = (value) => Math.round(value * phoneScale);
  const storyTopMargin = s(HP_PHONE_REF.storyTopMargin);
  // Padding horizontal de la rangée hero (FirstSectionContainer padding: 0 12px) + inset titre
  const heroRowOuterPad = 12;
  const heroRowWidth = rowWidth(phoneScale, heroTextToVisualGap, heroTextWidth, heroMediaGap);
  // Centrage dans la zone utile (viewport − pads), puis pad gauche + marginLeft titre
  const heroTitleLeft = Math.round(
    Math.max(0, (viewportWidth - heroRowOuterPad * 2 - heroRowWidth) / 2)
    + heroRowOuterPad
    + heroLeftInset
  );
  return {
    scale: phoneScale,
    deviceBgHeight: s(HP_PHONE_REF.deviceBgHeight),
    storyHeight: s(HP_PHONE_REF.storyHeight),
    storyTopMargin,
    frameWidth: s(HP_PHONE_REF.frameWidth),
    playerWidth: s(HP_PHONE_REF.playerWidth),
    frameMarginLeft: s(HP_PHONE_REF.frameMarginLeft),
    heroArrowWidth: s(HP_PHONE_REF.heroArrowWidth),
    heroCalloutMarginTop: storyTopMargin + s(HP_PHONE_REF.heroCalloutOffset),
    heroCalloutMaxWidth: s(HP_PHONE_REF.heroCalloutMaxWidth),
    heroTextWidth: Math.round(heroTextWidth),
    heroLeftInset,
    heroTitleLeft,
    heroTextToVisualGap: Math.round(heroTextToVisualGap),
    heroMediaGap: Math.round(heroMediaGap),
    heroTextMarginTop,
    waveReserve,
  };
}

function renderHpPhoneFrameOverlay({ frameWidth, deviceBgHeight, frameAlt = "", id }) {
  return (
    <img
      id={id}
      src="/images/transparent_framed.png"
      alt={frameAlt}
      draggable={false}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: frameWidth,
        height: deviceBgHeight,
        pointerEvents: "none",
        zIndex: 1,
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
      }}
    />
  );
}

const HP_PHONE_PLAYER_BLEED_PX = 1;

function getHpPhoneFrameShellStyle(frameWidth, deviceBgHeight) {
  return {
    width: frameWidth,
    height: deviceBgHeight,
    position: "relative",
    overflow: "hidden",
    flexShrink: 0,
    userSelect: "none",
    WebkitUserSelect: "none",
  };
}

function getHpPhonePlayerInsetStyle(frameMarginLeft, storyTopMargin) {
  return {
    marginLeft: Math.max(0, frameMarginLeft - HP_PHONE_PLAYER_BLEED_PX),
    position: "relative",
    top: Math.max(0, storyTopMargin - HP_PHONE_PLAYER_BLEED_PX),
  };
}

function getHpPhonePlayerBleedSize(playerWidth, storyHeight) {
  return {
    width: playerWidth + HP_PHONE_PLAYER_BLEED_PX * 2,
    height: storyHeight + HP_PHONE_PLAYER_BLEED_PX * 2,
  };
}

const INSTANT_AD_WHO_FOR_MOCKUP_CARDS = [
  { titleKey: "InstantAdWhoForRestaurantsTitle", detKey: "InstantAdWhoForRestaurantsDet", delay: "100", variant: "featured", decor: "bottomLeftCircle" },
  { titleKey: "InstantAdWhoForAgenciesTitle", detKey: "InstantAdWhoForAgenciesDet", delay: "200", variant: "default", decor: "rightDiamond" },
  { titleKey: "InstantAdWhoForChainTitle", detKey: "InstantAdWhoForChainDet", delay: "300", variant: "default", decor: "topRightCircle" },
];

function renderWhoForMockupDecor(decor, isFeatured, compact = false) {
  if (compact) {
    if (decor === "topRightCircle") {
      return (
        <div style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 72,
          height: 72,
          borderRadius: "50%",
          backgroundColor: "rgba(4, 211, 188, 0.08)",
          pointerEvents: "none",
        }} />
      );
    }
    if (decor === "bottomLeftCircle") {
      return (
        <div style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: 80,
          height: 80,
          borderRadius: "50%",
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          pointerEvents: "none",
        }} />
      );
    }
    return (
      <div style={{
        position: "absolute",
        top: "50%",
        right: 0,
        width: 56,
        height: 56,
        borderRadius: 8,
        transform: "translateY(-50%) rotate(45deg)",
        backgroundColor: isFeatured ? "rgba(255, 255, 255, 0.14)" : INSTANT_AD_BRAND_GREEN_SOFT,
        pointerEvents: "none",
      }} />
    );
  }

  if (decor === "topRightCircle") {
    return (
      <div style={{
        position: "absolute",
        top: -40,
        right: -40,
        width: 160,
        height: 160,
        borderRadius: "50%",
        backgroundColor: "rgba(4, 211, 188, 0.08)",
        pointerEvents: "none",
      }} />
    );
  }
  if (decor === "bottomLeftCircle") {
    return (
      <div style={{
        position: "absolute",
        bottom: -40,
        left: -40,
        width: 192,
        height: 192,
        borderRadius: "50%",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        pointerEvents: "none",
      }} />
    );
  }
  return (
    <div style={{
      position: "absolute",
      top: "50%",
      right: -48,
      width: 128,
      height: 128,
      borderRadius: 8,
      transform: "translateY(-50%) rotate(45deg)",
      backgroundColor: isFeatured ? "rgba(255, 255, 255, 0.14)" : INSTANT_AD_BRAND_GREEN_SOFT,
      pointerEvents: "none",
    }} />
  );
}

function renderWhoForMockupCard(t, { titleKey, detKey, delay, variant, decor }, compact = false) {
  const isFeatured = variant === "featured";
  return (
    <div
      key={titleKey}
      className={`hp-who-for-card scroll-benefit-item scroll-delay-${delay}`}
      style={{
        position: "relative",
        // clip (pas hidden) : masque le décor sans créer de scrollport qui avale la molette
        overflow: compact ? "visible" : "clip",
        padding: compact ? "28px 24px" : "40px",
        borderRadius: 24,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        textAlign: "left",
        flex: compact ? "1 1 auto" : "1 1 300px",
        width: compact ? "100%" : undefined,
        maxWidth: compact ? "100%" : 380,
        minWidth: 0,
        boxSizing: "border-box",
        background: isFeatured ? INSTANT_AD_BRAND_GREEN : "#ffffff",
        color: isFeatured ? "#ffffff" : "rgb(45, 55, 72)",
        border: isFeatured ? "none" : "1px solid rgba(187, 202, 198, 0.5)",
        boxShadow: isFeatured ? "0 25px 50px -12px rgba(4, 211, 188, 0.28)" : "none",
        zIndex: compact ? "auto" : (isFeatured ? 10 : 1),
      }}
    >
      {renderWhoForMockupDecor(decor, isFeatured, compact)}
      <h3 style={{
        margin: 0,
        marginBottom: compact ? 12 : 16,
        fontSize: compact ? 18 : 22,
        fontWeight: 700,
        lineHeight: 1.25,
        position: "relative",
        zIndex: 10,
      }}>
        {t(titleKey)}
      </h3>
      <p style={{
        margin: 0,
        fontSize: compact ? 14 : 15,
        lineHeight: 1.5,
        fontWeight: 400,
        color: isFeatured ? "rgba(255, 255, 255, 0.9)" : "#3c4947",
        position: "relative",
        zIndex: 10,
        whiteSpace: "pre-line",
      }}>
        {t(detKey)}
      </p>
    </div>
  );
}

function renderWhoForMockupSlide(t, { MaterialButton, analyticsManager, navigate, compact = false, denseTop = false, backgroundColor = INSTANT_AD_BG_WHITE }) {
  const shellCompact = compact || denseTop;
  const titleStyle = compact
    ? instantAdSlideTitleStyleCompact
    : denseTop
      ? { ...instantAdSlideTitleStyle, marginTop: 18, marginBottom: 46 }
      // desktop non-compact : même écart titre→contenu que début de slide→titre (shell 80)
      : { ...instantAdSlideTitleStyle, marginBottom: 80 };
  return (
    <div
      className="scroll-slide-up hp-who-for-slide"
      style={instantAdSlideShellStyle(backgroundColor, { compact: shellCompact })}
    >
      <h2 style={titleStyle}>
        {t("InstantAdWhoForTitle")}
      </h2>
      <div style={{
        display: "flex",
        flexDirection: compact ? "column" : "row",
        justifyContent: "center",
        alignItems: compact ? "stretch" : "stretch",
        gap: compact ? 16 : 32,
        maxWidth: 1200,
        width: "100%",
        margin: "0 auto",
        flexWrap: "wrap",
      }}>
        {INSTANT_AD_WHO_FOR_MOCKUP_CARDS.map((card) => renderWhoForMockupCard(t, card, compact))}
      </div>
      <div
        className="scroll-fade-up hp-who-for-cta"
        style={{
          marginTop: 48,
          display: "flex",
          flexDirection: compact ? "column" : "row",
          alignItems: "center",
          justifyContent: "center",
          gap: compact ? 20 : 16,
          flexWrap: "wrap",
        }}
      >
        <MaterialButton style="filled-brand-gradient" onClick={() => openCalendlyDemo(analyticsManager, "ad_scheduleDemoBtm")} value={t("CTA")} />
        <MaterialButton style="filled" sx={HP_LANDING_CTA_SX} onClick={() => { analyticsManager.logHPTryMakerTag("bottom_ad"); navigate("/maker"); }} value={t("InstantAdCTA")} />
      </div>
    </div>
  );
}

const INSTANT_AD_CREATE_STEPS = [
  { step: 1, titleKey: "InstantAdSubBenefit21", detVariant: "create1ClicLink", delay: "200" },
  { step: 2, titleKey: "InstantAdSubBenefit22", detKey: "InstantAdSubBenefit22det", delay: "250" },
  { step: 3, titleKey: "InstantAdSubBenefit23", detKey: "InstantAdSubBenefit23det", delay: "300" },
  { step: 4, titleKey: "InstantAdSubBenefit24", detKey: "InstantAdSubBenefit24det", delay: "350" },
  { step: 5, titleKey: "InstantAdSubBenefit25", detKey: "InstantAdSubBenefit25det", delay: "400" },
];

const INSTANT_AD_PLANCHE_ITEMS = [
  { emoji: "🎬", titleKey: "RestaurantsSubBenefit11", delay: "100", side: "left", topPct: 20 },
  { emoji: "📋", titleKey: "RestaurantsSubBenefit12", delay: "200", side: "left", topPct: 50, topPct2: 94, multiline: true },
  { emoji: "⭐", titleKey: "RestaurantsSubBenefit13", delay: "300", side: "right", topPct: 74, topPctCompactLaptop: 74 },
  { emoji: "💬", titleKey: "RestaurantsSubBenefit14", delay: "400", side: "right", topPct: 80, topPctCompactLaptop: 80 },
];

function plancheScrollItemClass(delay, animated = true) {
  return animated ? `scroll-benefit-item scroll-delay-${delay}` : undefined;
}

function renderPlancheMultilineLeftAnnotation(t, { titleKey, delay, topPct, topPct2 }, { storyTopMargin, storyHeight, compact = false, animated = true }) {
  const fullText = t(titleKey).replace(/\n/g, " ");
  const top1 = storyTopMargin + (storyHeight * topPct) / 100;
  const top2 = storyTopMargin + (storyHeight * topPct2) / 100;
  const lineGap = compact ? 10 : 14;
  const hLineWidth = compact ? 28 : 56;
  const dashedColor = HP_HERO_TEAL;
  const dashedOpacity = 0.7;
  const labelStyle = {
    fontSize: compact ? 13 : 15,
    fontWeight: 600,
    color: "rgb(45, 55, 72)",
    lineHeight: 1.35,
    textAlign: "left",
    whiteSpace: "nowrap",
  };
  const hLineStyle = {
    width: hLineWidth,
    flexShrink: 0,
    borderTop: `2px dashed ${dashedColor}`,
    opacity: dashedOpacity,
  };

  return (
    <div key={titleKey} className={plancheScrollItemClass(delay, animated)} style={{ position: "absolute", inset: 0, zIndex: 2 }}>
      <div
        style={{
          position: "absolute",
          top: top1,
          height: Math.max(top2 - top1, 0),
          right: hLineWidth,
          borderLeft: `2px dashed ${dashedColor}`,
          opacity: dashedOpacity,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: top1,
          left: 0,
          right: 0,
          transform: "translateY(-50%)",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <div style={labelStyle}>{fullText}</div>
        <div style={{ width: lineGap, flexShrink: 0 }} />
        <div style={hLineStyle} />
      </div>
      <div
        style={{
          position: "absolute",
          top: top2,
          right: 0,
          transform: "translateY(-50%)",
          ...hLineStyle,
        }}
      />
    </div>
  );
}

function renderPlancheSideAnnotation(t, { titleKey, delay, side, topPct, topPctCompactLaptop }, { storyTopMargin, storyHeight, compact = false, compactLaptop = false, animated = true }) {
  const isLeft = side === "left";
  const effectiveTopPct = compactLaptop && topPctCompactLaptop != null ? topPctCompactLaptop : topPct;
  const anchorTop = storyTopMargin + (storyHeight * effectiveTopPct) / 100;
  const tight = compact || compactLaptop;
  const lineGap = tight ? 8 : 14;
  const label = t(titleKey).replace(/\n/g, " ");
  const labelStyle = {
    fontSize: tight ? 13 : 15,
    fontWeight: 600,
    color: "rgb(45, 55, 72)",
    lineHeight: 1.35,
    textAlign: "left",
    whiteSpace: "nowrap",
    flexShrink: 0,
  };
  const lineStyle = {
    flex: "1 1 auto",
    minWidth: tight ? 8 : 20,
    maxWidth: tight ? 24 : 56,
    borderTop: `2px dashed ${HP_HERO_TEAL}`,
    opacity: 0.7,
    flexShrink: 1,
  };
  const calloutStyle = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flex: "0 0 auto",
  };

  return (
    <div
      key={titleKey}
      className={plancheScrollItemClass(delay, animated)}
      style={{
        position: "absolute",
        top: anchorTop,
        left: 0,
        right: 0,
        transform: "translateY(-50%)",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        zIndex: 2,
      }}
    >
      {isLeft ? (
        <>
          <div style={{ ...calloutStyle, flex: "1 1 auto", justifyContent: "flex-end", minWidth: 0 }}>
            <div style={labelStyle}>{label}</div>
          </div>
          <div style={{ width: lineGap, flexShrink: 0 }} />
          <div style={lineStyle} />
        </>
      ) : (
        <>
          <div style={lineStyle} />
          <div style={{ width: lineGap, flexShrink: 0 }} />
          <div style={{ ...calloutStyle, justifyContent: "flex-start" }}>
            <div style={labelStyle}>{label}</div>
          </div>
        </>
      )}
    </div>
  );
}

function renderPlancheMobileFeatureItem(t, { emoji, titleKey, delay }, animated = true) {
  return (
    <div
      key={titleKey}
      className={plancheScrollItemClass(delay, animated)}
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        width: "100%",
        boxSizing: "border-box",
        textAlign: "left",
      }}
    >
      <span style={{ fontSize: 22, flexShrink: 0, lineHeight: 1, display: "flex", alignItems: "center" }}>{emoji}</span>
      <div style={{
        fontSize: 15,
        fontWeight: 600,
        color: "rgb(45, 55, 72)",
        lineHeight: 1.45,
        textAlign: "left",
        flex: 1,
        minWidth: 0,
      }}>
        {t(titleKey).replace(/\n/g, " ")}
      </div>
    </div>
  );
}

function renderPlancheDiscoveryLayout({
  t,
  Player,
  deviceBgHeight,
  storyTopMargin,
  storyHeight,
  compact = false,
  frameWidth = 274,
  playerWidth = 244,
  frameMarginLeft = 14,
  compactLaptop = false,
  animated = true,
  storyId = "cafeuprealitychain1",
  /** Même X que « La landing page du réseau social » (diapo 1). */
  titleLeft = 24,
  viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1280,
}) {
  const phoneFrameProps = { frameWidth, playerWidth, frameMarginLeft, storyId };
  const annotationLayout = { storyTopMargin, storyHeight, compact, compactLaptop, animated };
  const phoneScrollClass = animated ? "scroll-scale-in" : "";
  const phoneScrollDelay = animated ? "200" : "";
  // Marge mini pour ne pas chevaucher les vagues latérales (+ petit gap)
  const waveTitleClearance = Math.max(
    16,
    Math.round((viewportWidth * HP_PANORAMA_SIDE_PATH_W * HP_PANORAMA_SIDE_SCALE) / HP_PANORAMA_VIEWBOX_W)
  ) + 8;

  if (compact) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: 420, margin: "0 auto" }}>
        <div style={{ width: "100%", display: "flex", justifyContent: "center", textAlign: "center" }}>
          {renderHpPlancheTitle(t, { compact: true, centered: true })}
        </div>
        <div style={{ display: "flex", justifyContent: "center", marginTop: 28 }}>
          {renderPlanchePhoneFrame({
            Player,
            t,
            deviceBgHeight,
            storyTopMargin,
            storyHeight,
            scrollClass: phoneScrollClass,
            scrollDelay: phoneScrollDelay,
            ...phoneFrameProps,
          })}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%", maxWidth: 320, margin: "24px auto 0" }}>
          {INSTANT_AD_PLANCHE_ITEMS.map((item) => renderPlancheMobileFeatureItem(t, item, animated))}
        </div>
      </div>
    );
  }

  // Spacious : X hero un cran à gauche (titre seul — le bloc téléphone/annotations ne bouge pas).
  // Compact : clearance vague (+ petit gap) pour ne pas chevaucher.
  const effectiveTitleLeft = compactLaptop
    ? waveTitleClearance
    : Math.max(0, titleLeft - 28);
  const sideColumnStyle = {
    flex: compactLaptop ? "0 1 200px" : "1 1 220px",
    width: compactLaptop ? 200 : undefined,
    maxWidth: compactLaptop ? 200 : 300,
    minWidth: compactLaptop ? 160 : 160,
    position: "relative",
    height: deviceBgHeight,
    flexShrink: compactLaptop ? 1 : 0,
    overflow: "visible",
  };

  // Desktop : titre au X hero (ou marge réduite en compact) ; coque + annotations à droite
  return (
    <div
      style={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        margin: 0,
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        gap: compactLaptop ? 12 : 40,
        boxSizing: "border-box",
        paddingRight: compactLaptop ? 8 : 0,
      }}
    >
      <div
        className="scroll-hero"
        style={{
          flex: compactLaptop ? "1 1 340px" : "0 1 480px",
          maxWidth: compactLaptop ? 420 : 480,
          minWidth: compactLaptop ? 280 : 0,
          marginLeft: effectiveTitleLeft,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          alignSelf: "flex-start",
          // Même position relative à la coque quel que soit le scale (évite titre trop haut en compact)
          marginTop: Math.round(deviceBgHeight * 0.21),
        }}
      >
        {renderHpPlancheTitle(t, { compact: compactLaptop })}
      </div>
      <div
        style={{
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: compactLaptop ? "flex-end" : "center",
          gap: compactLaptop ? 4 : 12,
          minWidth: 0,
          overflow: "visible",
        }}
      >
        <div style={sideColumnStyle}>
          {INSTANT_AD_PLANCHE_ITEMS.filter((item) => item.side === "left").map((item) =>
            item.multiline
              ? renderPlancheMultilineLeftAnnotation(t, item, annotationLayout)
              : renderPlancheSideAnnotation(t, item, annotationLayout)
          )}
        </div>
        {renderPlanchePhoneFrame({
          Player,
          t,
          deviceBgHeight,
          storyTopMargin,
          storyHeight,
          scrollClass: phoneScrollClass,
          scrollDelay: phoneScrollDelay,
          ...phoneFrameProps,
        })}
        <div style={sideColumnStyle}>
          {INSTANT_AD_PLANCHE_ITEMS.filter((item) => item.side === "right").map((item) =>
            renderPlancheSideAnnotation(t, item, annotationLayout)
          )}
        </div>
      </div>
    </div>
  );
}

function renderPlanchePhoneFrame({
  Player,
  t,
  deviceBgHeight,
  storyTopMargin,
  storyHeight,
  frameWidth = 274,
  playerWidth = 244,
  frameMarginLeft = 14,
  scrollClass = "",
  scrollDelay = "",
  storyId = "cafeuprealitychain1",
}) {
  const wrapperClass = [scrollClass, scrollDelay ? `scroll-delay-${scrollDelay}` : ""].filter(Boolean).join(" ");
  return (
    <div className={wrapperClass || undefined} style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ position: "relative", width: frameWidth, height: deviceBgHeight }}>
        <div style={getHpPhoneFrameShellStyle(frameWidth, deviceBgHeight)}>
          <div style={getHpPhonePlayerInsetStyle(frameMarginLeft, storyTopMargin)}>
            <Player
              fromMaker={false}
              fromDemo={"middle"}
              mini={true}
              storyId={storyId}
              windowDimensions={getHpPhonePlayerBleedSize(playerWidth, storyHeight)}
            />
          </div>
          {renderHpPhoneFrameOverlay({ frameWidth, deviceBgHeight, frameAlt: t("PhoneFrameAlt") })}
        </div>
      </div>
    </div>
  );
}

function renderInstantAdCreateStepsList(t, { analyticsManager, setShowCreate1Clic } = {}) {
  return (
    <div style={{ width: "100%", maxWidth: 680, alignSelf: "stretch", textAlign: "left" }}>
      {INSTANT_AD_CREATE_STEPS.map((item, index) => renderInstantAdCreateStep(t, item, index === INSTANT_AD_CREATE_STEPS.length - 1, { analyticsManager, setShowCreate1Clic }))}
    </div>
  );
}

function renderInstantAdCreateStepDet(t, { detKey, detVariant }, { analyticsManager, setShowCreate1Clic } = {}) {
  if (detVariant === "create1ClicLink") {
    if (setShowCreate1Clic) {
      const openCreate1ClicDialog = () => {
        analyticsManager?.logOpenPopupTag(null, "create1Click_hp_benefit21", true);
        setShowCreate1Clic("create1Click_hp_benefit21");
      };
      return (
        <>
          {t("InstantAdSubBenefit21detPrefix")}
          <button
            type="button"
            onClick={openCreate1ClicDialog}
            style={{
              font: "inherit",
              fontStyle: "inherit",
              color: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
              background: "none",
              border: "none",
              padding: 0,
            }}
          >
            {t("InstantAdSubBenefit21detLink")}
          </button>
          {t("InstantAdSubBenefit21detSuffix")}
        </>
      );
    }
    return `${t("InstantAdSubBenefit21detPrefix")}${t("InstantAdSubBenefit21detLink")}${t("InstantAdSubBenefit21detSuffix")}`;
  }
  return detKey ? t(detKey) : null;
}

function renderInstantAdCreateStep(t, { step, titleKey, detKey, detVariant, delay }, isLast = false, dialogHandlers = {}) {
  const detContent = renderInstantAdCreateStepDet(t, { detKey, detVariant }, dialogHandlers);
  return (
    <div
      key={step}
      className={`scroll-benefit-item scroll-delay-${delay}`}
      style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 18, marginBottom: isLast ? 0 : 4, width: "100%" }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 32 }}>
        <div style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: HP_HERO_TEAL,
          color: "#ffffff",
          fontWeight: 700,
          fontSize: 15,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          {step}
        </div>
        {!isLast ? (
          <div style={{ width: 2, flex: 1, minHeight: 20, marginTop: 6, backgroundColor: "#d9e2ec", borderRadius: 1 }} />
        ) : null}
      </div>
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.45, paddingBottom: isLast ? 0 : 18, paddingTop: 2, textAlign: "left" }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "rgb(45, 55, 72)" }}>{t(titleKey)}</div>
        {detContent ? (
          <div style={{ fontSize: 15, color: "#718096", marginTop: 6, fontStyle: "italic", lineHeight: 1.5 }}>
            {detContent}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export const MainHPConfig = {
  homePageOrigin: null,
  pageTitle: 'ProductPageAdTitle',
  origin: 'home_page_ad',
  hasWebDemo: true, // Cette page a maintenant la section de comparaison avec web_demo
  
  // Configuration FAQ
  faqConfig: [
    { theme: "socialAdDest" },
    { theme: "socialWebsiteVideo" },
    { theme: "socialProfileRedirect" },
    { theme: "alreadyWebsite" },
    { theme: "easyToUse" }
  ],
  
  // Configuration des données FAQ
  faqData: (t) => ({
    title: t("FAQCatUprealityOffer"),
    rows: [
      {
        title: t("FAQSocialAdDestQuestion"),
        content: t("FAQSocialAdDestAnswer"),
      },
      {
        title: t("FAQSocialWebsiteVideoQuestion"),
        content: t("FAQSocialWebsiteVideoAnswer"),
      },
      {
        title: t("FAQSocialProfileRedirectQuestion"),
        content: [
          t("FAQSocialProfileRedirectAnswer"),
          "\n\n- " + t("FAQSocialProfileRedirectAnswerl0"),
          "\n- " + t("FAQSocialProfileRedirectAnswerl1"),
          "\n- " + t("FAQSocialProfileRedirectAnswerl2"),
          "\n\n" + t("FAQSocialProfileRedirectAnswerAfter"),
        ].join(""),
      },
      {
        title: t("FAQWebsiteSubstituteQuestion"),
        content: t("FAQWebsiteSubstituteAnswer"),
      },
      {
        title: t("FAQEasyToUseQuestion"),
        content: t("FAQEasyToUseAnswer"),
      },
    ],
  }),

  // Schema.org FAQPage pour GEO
  faqSchema: (t) => {
    const d = MainHPConfig.faqData(t);
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: d.rows.map((r) => ({
        "@type": "Question",
        name: r.title,
        acceptedAnswer: { "@type": "Answer", text: r.content },
      })),
    };
  },

  // Rendu desktop
  desktopRenderer: (props) => {
    const { 
      t, navigate, language, analyticsManager, MaterialButton, ImageModal, 
      Player, WebPageMock, TestimonialsSection, Faq, styles,
      showLogin, setShowLogin, loginTitle, setLoginTitle, showCreate1Clic, setShowCreate1Clic, isDesktopDemoHovered, setIsDesktopDemoHovered, onLogin,
      viewportHeight = window.innerHeight,
      viewportWidth = window.innerWidth,
    } = props;
    const langPrefix = language === "fr" ? "/fr" : "/en";

    // Fonctions pour les analytics
    const handleWebDemoClick = () => {
      if (analyticsManager && analyticsManager.logClickInWebDemoTag) {
        analyticsManager.logClickInWebDemoTag();
      }
    };

    const handleWebDemoScroll = () => {
      if (analyticsManager && analyticsManager.logScrollInWebDemoTag) {
        analyticsManager.logScrollInWebDemoTag();
      }
    };

    const deskTop = viewportHeight >= 80 + 16 + 576 + 16;
    const logoHeight = 42;
    const topbarPaddingY = 16;
    const topbarHeight = topbarPaddingY * 2 + logoHeight;
    const compareSlideCompactDesktop = viewportHeight >= HP_DESKTOP_VISUAL_COMPACT_MAX_HEIGHT;
    const compactLaptop = !compareSlideCompactDesktop;
    const {
      deviceBgHeight,
      storyHeight,
      storyTopMargin,
      frameWidth,
      playerWidth,
      frameMarginLeft,
      createSlideImageHeight,
      createSlideVideoWidth,
    } = getHpDesktopVisualMetrics(deskTop, { compactLaptop });
    const planche = getHpPlancheFitVisualMetrics({ viewportHeight, compactLaptop });
    // Diapo 1 : coques scalées pour remplir le plein écran (hauteur + largeur)
    const hero = getHpHeroFitVisualMetrics({
      viewportHeight,
      viewportWidth,
      topbarHeight,
      compactLaptop,
    });
    const {
      deviceBgHeight: heroDeviceBgHeight,
      storyHeight: heroStoryHeight,
      storyTopMargin: heroStoryTopMargin,
      frameWidth: heroFrameWidth,
      playerWidth: heroPlayerWidth,
      frameMarginLeft: heroFrameMarginLeft,
      heroArrowWidth,
      heroCalloutMarginTop,
      heroCalloutMaxWidth,
      heroTextWidth,
      heroLeftInset,
      heroTitleLeft,
      heroTextToVisualGap,
      heroMediaGap,
      heroTextMarginTop,
      waveReserve: heroWaveReserve,
    } = hero;
    const compareTitleWrapperStyle = compareSlideCompactDesktop
      ? { flexShrink: 0, width: '100%', paddingTop: 80, paddingBottom: 80 }
      : { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, width: '100%', padding: '28px 0', boxSizing: 'border-box' };
    const compareTitleStyle = {
      ...instantAdSlideTitleStyle,
      margin: compareSlideCompactDesktop ? 0 : '18px 0',
      width: '100%',
      flexShrink: 0,
    };
    // Compact : haut = 28 shell + 18 marge ; bas = 46 (comme slide 2). Non-compact : 80 / 80 (comme slide 2 padding titre)
    const slideTitleStyle = compactLaptop
      ? { ...instantAdSlideTitleStyle, marginTop: 18, marginBottom: 46 }
      : { ...instantAdSlideTitleStyle, marginBottom: 80 };
    const slideShellOpts = { compact: compactLaptop };
    const floatingIndicatorWrapperStyle = {
      position: "absolute",
      top: "30%",
      right: 0,
      transform: "translateY(-50%)",
      zIndex: 30,
      overflow: "hidden",
      width: "fit-content",
      display: "flex",
      justifyContent: "flex-end",
      pointerEvents: "none",
    };
    const floatingIndicatorStyle = {
      transform: isDesktopDemoHovered ? "translateX(105%)" : "translateX(0)",
      transition: "transform 180ms ease-out",
      width: "fit-content",
      display: "inline-block",
      zIndex: 30,
      backgroundColor: "#ffffff",
      border: "1px solid #d9efe9",
      borderRight: "none",
      borderTopLeftRadius: compactLaptop ? 12 : 14,
      borderBottomLeftRadius: compactLaptop ? 12 : 14,
      padding: compactLaptop ? "12px 18px 12px 14px" : "15px 22px 15px 16px",
      boxShadow: "0 10px 24px rgba(4, 211, 188, 0.18)",
      fontSize: compactLaptop ? 14 : 16,
      fontWeight: 700,
      color: "#1f2d3d",
      whiteSpace: "nowrap",
      pointerEvents: "none",
    };
    const floatingIndicatorIconSize = compactLaptop ? 20 : 24;
    const floatingIndicatorGlyphSize = compactLaptop ? 14 : 16;
    const floatingIndicatorId = "homepage-floating-indicator";
    const isSafariMacOS = isMacOS() && isSafari();
    const hasRectOverlap = (rectA, rectB) => {
      return !(
        rectA.right < rectB.left ||
        rectA.left > rectB.right ||
        rectA.bottom < rectB.top ||
        rectA.top > rectB.bottom
      );
    };
    const dismissFloatingIndicator = () => {
      setIsDesktopDemoHovered(true);
    };
    const handleDemoMouseEnter = (e) => {
      // Safari macOS : une fois masquée, ne pas réévaluer (évite le retour à l'ouverture des options)
      if (isSafariMacOS && isDesktopDemoHovered) {
        return;
      }
      const indicator = document.getElementById(floatingIndicatorId);
      if (!indicator) {
        if (!isSafariMacOS) {
          setIsDesktopDemoHovered(false);
        }
        return;
      }
      const demoRect = e.currentTarget.getBoundingClientRect();
      const indicatorRect = indicator.getBoundingClientRect();
      const overlaps = hasRectOverlap(demoRect, indicatorRect);
      if (isSafariMacOS) {
        if (overlaps) {
          dismissFloatingIndicator();
        }
      } else {
        setIsDesktopDemoHovered(overlaps);
      }
    };

    return (
      <div style={{display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'center', alignContent: 'center', justifyContent: 'center', background: HP_SLIDE_BG_HERO, color: "rgb(45, 55, 72)" }}>
        <div style={floatingIndicatorWrapperStyle}>
          <div id={floatingIndicatorId} style={floatingIndicatorStyle}>
            <div style={{display: "flex", alignItems: "center", gap: compactLaptop ? 7 : 8}}>
              <div style={{width: floatingIndicatorIconSize, height: floatingIndicatorIconSize, borderRadius: "50%", backgroundColor: HP_HERO_TEAL, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0}}>
                <TrendingUpIcon style={{fontSize: floatingIndicatorGlyphSize, color: "#ffffff"}} />
              </div>
              <span>{t("HomeFloatingConversionTarget")}</span>
            </div>
          </div>
        </div>
        <div
          className="hp-hero-slide"
          style={{
            position: "relative",
            width: "100%",
            height: viewportHeight,
            minHeight: viewportHeight,
            maxHeight: viewportHeight,
            display: "flex",
            flexDirection: "column",
            background: HP_SLIDE_BG_HERO,
            boxSizing: "border-box",
            overflow: "clip",
          }}
        >
          <div style={{position: "relative", zIndex: 2, width: "100%", padding: `${topbarPaddingY}px clamp(16px, 4vw, 76px)`, boxSizing: "border-box", background: "transparent", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", flexShrink: 0}}>
            <div style={{display: "flex", flexDirection: "row", alignItems: "center", cursor: "pointer", flexShrink: 0}} onClick={()=>{analyticsManager.logClickLogoTag();navigate(`${langPrefix}`);}}>
              <img src="/images/logo_gradient_transparent.png" alt="UpReality" height={logoHeight} style={{display: "block", height: logoHeight, width: "auto"}} />
            </div>
            <div style={{display: "flex", flexDirection: "row", alignContent: "center", justifyContent: "flex-end", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
              <button type="button" className="topbar-btn topbar-btn-active" aria-current="page">{t("TopbarHome")}</button>
              <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{analyticsManager.logClickPricingTag();navigate("/Pricing");}}>{t("Pricing")}</button>
              <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{navigate(`${langPrefix}/about`);}}>{t("AboutUs")}</button>
              <div style={{width: 7, height: 7, borderRadius: "50%", backgroundColor: "#04d3bc", margin: "0 6px"}} />
              <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{analyticsManager.logOpenPopupTag(null, "home_ad_login", true);setShowLogin("login");setLoginTitle(t('Login'));}}>{t("Login")}</button>
              <button type="button" className="topbar-btn topbar-btn-primary" onClick={()=>{analyticsManager.logOpenPopupTag(null, "home_ad_create_account", true);setShowLogin("account");setLoginTitle(t('CreateAccount'));}}>{t("CreateAccount")}</button>
            </div>
          </div>
          <div className="FirstSectionContainer" style={{position: "relative", zIndex: 2, flex: 1, minHeight: 0, width: '100%', display: "flex", flexDirection: "column", justifyContent: 'center', background: "transparent", boxSizing: "border-box", paddingBottom: heroWaveReserve}}>
            <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-start', width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: '0 12px'}}>
              <div className="scroll-hero" style={{width: heroTextWidth, maxWidth: heroTextWidth, flexShrink: 1, minWidth: 0, marginTop: heroTextMarginTop, marginLeft: heroLeftInset}}>
                {renderHpHeroTitle(t, { compact: compactLaptop || heroTextWidth < 420 })}
                <div className="scroll-fade-up scroll-delay-200" style={{ marginTop: heroTextWidth < 420 ? 14 : 22, maxWidth: Math.min(480, heroTextWidth), color: "#687b7d", fontSize: compactLaptop || heroTextWidth < 420 ? 14 : 17, lineHeight: 1.5 }}>
                  {t("InstantAdHero41")} {t("InstantAdHero42")}
                </div>
                <div className="scroll-cta scroll-delay-300" style={{ marginTop: compactLaptop || heroTextWidth < 420 ? 16 : 28, display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                  <MaterialButton style="filled-brand-gradient" onClick={() => openCalendlyDemo(analyticsManager, "ad_scheduleDemo")} value={t("CTA")} />
                  <MaterialButton style="filled" sx={HP_LANDING_CTA_SX} onClick={()=>{analyticsManager.logHPTryMakerTag("top_ad");navigate("/maker");}}  value={t("InstantAdCTA")} />
                </div>
              </div>
              <div className="scroll-fade-right scroll-delay-200" style={{ flexShrink: 0 }}>
                <div style={{marginLeft: heroTextToVisualGap}}>
                  <img src={"/images/from_ig_ig_profile_AD.webp"} alt={t('FromInstagramProfileAlt')} style={{height: heroDeviceBgHeight}} />
                </div>
              </div>
              {<div
                className="scroll-scale-in scroll-delay-400"
                style={{position: "relative", marginLeft: heroMediaGap, display: "flex", flexDirection: "row", alignItems: "flex-start", flexShrink: 0}}
                onMouseEnter={handleDemoMouseEnter}
                onMouseLeave={isSafariMacOS ? undefined : () => setIsDesktopDemoHovered(false)}
                onClick={isSafariMacOS ? dismissFloatingIndicator : undefined}
              >
                <div style={getHpPhoneFrameShellStyle(heroFrameWidth, heroDeviceBgHeight)}>
                  <div style={getHpPhonePlayerInsetStyle(heroFrameMarginLeft, heroStoryTopMargin)}>
                    <Player fromMaker={false} fromDemo={"top"} mini={true} storyId={"cafeuprealityad"} windowDimensions={getHpPhonePlayerBleedSize(heroPlayerWidth, heroStoryHeight)} />
                  </div>
                  {renderHpPhoneFrameOverlay({ frameWidth: heroFrameWidth, deviceBgHeight: heroDeviceBgHeight, frameAlt: t("PhoneFrameAlt") })}
                </div>
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    marginLeft: 12,
                    marginTop: heroCalloutMarginTop,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    maxWidth: heroCalloutMaxWidth,
                    pointerEvents: "none",
                  }}
                >
                  <img
                    src="/images/top_left_arrow.png"
                    alt=""
                    style={{width: heroArrowWidth, height: "auto", marginBottom: 6}}
                  />
                  <div style={{fontSize: 15, fontWeight: 700, color: "#1f2d3d", lineHeight: 1.35}}>
                    {t("InstantAdYourContentHere")}
                  </div>
                  <div style={{fontSize: 13, fontWeight: 500, color: "#64748b", lineHeight: 1.35, marginTop: 2}}>
                    {t("InstantAdYourContentNotAI")}
                  </div>
                </div>
              </div>}
            </div>
          </div>
          {renderHpHeroWaves({ height: heroWaveReserve + 24 })}
        </div>
        {
          <div className="scroll-slide-up" style={{marginTop: 0, minHeight: viewportHeight, width: '100%', backgroundColor: INSTANT_AD_BG_WHITE, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', paddingBottom: 24}}>
            <StructuredData id="compare-instant-ad" data={{
              "@context": "https://schema.org",
              "@type": "Table",
              "about": { "@type": "Thing", "name": t('CompareWebsiteRestaurants') },
              "abstract": `${t('WebsiteQ1')}, ${t('WebsiteQ2')}, ${t('WebsiteQ3')} | ${t('StoryQ1')}, ${t('StoryQ2')}, ${t('StoryQ3')}`
            }} />
            <div style={compareTitleWrapperStyle}>
              <h2 style={compareTitleStyle}>{t('CompareWebsiteRestaurants')}</h2>
            </div>
            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 16, padding: '0 24px', boxSizing: 'border-box' }}>
              <div style={{display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center', alignContent: 'center', justifyContent: 'center', flexShrink: 0, gap: 48}}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={getHpPhoneFrameShellStyle(frameWidth, deviceBgHeight)}>
                    <div style={getHpPhonePlayerInsetStyle(frameMarginLeft, storyTopMargin)}>
                      <WebPageMock 
                        id="web_demo" 
                        open={true} 
                        url={`/demoWeb2/index.html?lang=${detectBrowserLanguage()}`} 
                        width={getHpPhonePlayerBleedSize(playerWidth, storyHeight).width}
                        height={getHpPhonePlayerBleedSize(playerWidth, storyHeight).height}
                        imageOverWebsite={false} 
                        close={()=>{}}
                        onClick={handleWebDemoClick}
                        onScroll={handleWebDemoScroll}
                      />
                    </div>
                    {renderHpPhoneFrameOverlay({ frameWidth, deviceBgHeight })}
                  </div>
                  <div style={{marginTop: 10, fontSize: 14, fontStyle: 'italic', color: '#8a94a6', textAlign: 'center'}}>{t('CompareFrameTraditionalWebsite')}</div>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 64, maxWidth: 560, minWidth: 420}}>
                  {[
                    ['CompareDuel1', 'WebsiteQ1', 'StoryQ1'],
                    ['CompareDuel2', 'WebsiteQ2', 'StoryQ2'],
                    ['CompareDuel3', 'WebsiteQ3', 'StoryQ3'],
                  ].map(([label, left, right]) => (
                    <div key={label} style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                      <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 8}}>
                        <div style={{width: 96, borderTop: '1px dashed #c9d4e0', opacity: 0.6}}/>
                        <div style={{backgroundColor: 'rgba(4, 211, 188, 0.18)', color: '#0a9c8b', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 2, padding: '6px 16px', borderRadius: 999, whiteSpace: 'nowrap'}}>{t(label)}</div>
                        <div style={{width: 96, borderTop: '1px dashed #c9d4e0', opacity: 0.6}}/>
                      </div>
                      <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%', textAlign: 'center'}}>
                        <div style={{width: '50%', paddingRight: 16, boxSizing: 'border-box', fontStyle: 'italic', color: '#8a94a6', lineHeight: 1.4}}>{t(left)}</div>
                        <div style={{width: '50%', paddingLeft: 16, boxSizing: 'border-box', fontWeight: 700, color: 'rgb(45, 55, 72)', lineHeight: 1.4}}>{t(right)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={getHpPhoneFrameShellStyle(frameWidth, deviceBgHeight)}>      
                    <div style={getHpPhonePlayerInsetStyle(frameMarginLeft, storyTopMargin)}>                                                  
                      <Player fromMaker={false} fromDemo={"bottom"} mini={true} storyId={"cafeupreality"} windowDimensions={getHpPhonePlayerBleedSize(playerWidth, storyHeight)} />
                    </div>
                    {renderHpPhoneFrameOverlay({ frameWidth, deviceBgHeight })}
                  </div>
                  <div style={{marginTop: 10, fontSize: 14, fontStyle: 'italic', color: '#8a94a6', textAlign: 'center'}}>{t('CompareFrameSocialWebsite')}</div>
                </div> 
              </div>
              <CompareWebsiteClosing compact spacious={deskTop} variant="restaurants" />
            </div>
            {compareSlideCompactDesktop && <div style={{ flex: 1, minHeight: 0 }} />}
          </div>
        }
        <div
          className="scroll-slide-up hp-planche-slide"
          style={{
            position: "relative",
            marginTop: 0,
            width: "100%",
            backgroundColor: HP_SLIDE_BG_HERO,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: viewportHeight,
            // clip (pas hidden) : masque les vagues sans créer de scrollport qui avale la molette
            overflow: "clip",
            paddingTop: planche.padTop,
            paddingBottom: planche.padBottom,
            // Gauche : 0 — le titre gère sa marge. Droite : inset vagues (réduit en compact pour les annotations).
            paddingLeft: 0,
            paddingRight: compactLaptop ? "max(12px, calc(100% * 183 * 0.4 / 1440))" : HP_PANORAMA_SIDE_INSET,
          }}
        >
          {renderHpDiscoveryPanoramaWaves()}
          {renderPlancheDiscoveryLayout({
            t,
            Player,
            deviceBgHeight: planche.deviceBgHeight,
            storyTopMargin: planche.storyTopMargin,
            storyHeight: planche.storyHeight,
            frameWidth: planche.frameWidth,
            playerWidth: planche.playerWidth,
            frameMarginLeft: planche.frameMarginLeft,
            compactLaptop,
            titleLeft: heroTitleLeft,
            viewportWidth,
          })}
        </div>
        <div className="scroll-slide-up" style={instantAdSlideShellStyle(INSTANT_AD_BG_WHITE, slideShellOpts)}>
          <h2 style={slideTitleStyle}>
            {t("InstantAdBenefit2")}
          </h2>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
            <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", paddingRight: 40, minWidth: 0 }}>
              <div className="scroll-fade-left scroll-delay-100" style={{ flex: "0 0 auto" }}>
                <ImageContainer 
                  src={language === "fr" ? "/images/HP-screenshot2.webp" : "/images/HP-screenshot2-en.webp"}
                  alt={t('StoryMakerPagePreviewAlt')}
                  height={createSlideImageHeight}
                  variant="clean"
                  delay="100"
                  videoSrc={encodeURI("/images/creer_sa_landing_page_sociale.mp4")}
                  videoWidth={createSlideVideoWidth}
                  videoAnalyticsOrigin="home_ad_maker_video"
                />
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", justifyContent: "flex-start", paddingLeft: 40, minWidth: 0 }}>
              <div style={{ paddingTop: 4, width: "100%" }}>
                {renderInstantAdCreateStepsList(t, { analyticsManager, setShowCreate1Clic })}
              </div>
            </div>
          </div>
        </div>
        {renderWhoForMockupSlide(t, { MaterialButton, analyticsManager, navigate, denseTop: compactLaptop, backgroundColor: HP_SLIDE_BG_WHO_FOR })}
        <div
          className="scroll-slide-up"
          style={{
            marginTop: 0,
            width: "100%",
            backgroundColor: INSTANT_AD_BG_WHITE,
            minHeight: viewportHeight,
            height: viewportHeight,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            boxSizing: "border-box",
            overflow: "clip",
          }}
        >
          <TestimonialsSection badgeKey="KFontaineTestimonialBadge" compact={compactLaptop} />
        </div>
        <div className="scroll-slide-up" style={instantAdSlideShellStyle(HP_SLIDE_BG_WHO_FOR, slideShellOpts)}>
          <div style={{ width: 800, maxWidth: "100%", boxSizing: "border-box", marginTop: compactLaptop ? 18 : 0 }}>
            <StructuredData id="faq-instant-ad" data={MainHPConfig.faqSchema(t)} />
            <div className="hp-faq-on-grey">
              <Faq
                data={MainHPConfig.faqData(t)}
                styles={styles}
                config={{}}
              />
            </div>
          </div>
          <div className="scroll-fade-up" style={{ marginTop: 48, display: 'flex', flexDirection: 'row', justifyContent: 'center', width: "100%" }}>
            <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
              <MaterialButton style="filled-brand-gradient" onClick={() => openCalendlyDemo(analyticsManager, "ad_scheduleDemoFaq")} value={t("CTA")} />
              <div style={{marginTop: 20}}/>
              <MaterialButton style="filled" sx={HP_LANDING_CTA_SX} onClick={()=>{analyticsManager.logHPTryMakerTag("faq_ad");navigate("/maker");}}  value={t("InstantAdCTA")} />
            </div>
          </div>
        </div>
        <HpFooter analyticsManager={analyticsManager} />
      </div>
    );
  },

  // Rendu mobile
  mobileRenderer: (props) => {
    const { 
      t, navigate, language, analyticsManager, MaterialButton, 
      Player, WebPageMock, TestimonialsSection, Faq, styles,
      showLogin, setShowLogin, loginTitle, setLoginTitle, showCreate1Clic, setShowCreate1Clic, onLogin,
      viewportWidth = typeof window !== "undefined" ? window.innerWidth : 390,
    } = props;
    // smartphone uniquement (pas tablette) — même seuil que ListStories + HpFooter (@media 768px)
    const isPhone = viewportWidth <= 768;
    const langPrefix = language === "fr" ? "/fr" : "/en";
    const floatingIndicatorStyle = {
      position: "absolute",
      top: 370,
      right: 0,
      transform: "translateY(-50%)",
      zIndex: 30,
      backgroundColor: "#ffffff",
      border: "1px solid #d9efe9",
      borderRight: "none",
      borderTopLeftRadius: 14,
      borderBottomLeftRadius: 14,
      padding: "10px 15px 10px 10px",
      boxShadow: "0 10px 24px rgba(4, 211, 188, 0.18)",
      fontSize: 13,
      fontWeight: 700,
      color: "#1f2d3d",
      whiteSpace: "nowrap",
      pointerEvents: "none",
    };

    // Fonctions pour les analytics
    const handleWebDemoClick = () => {
      if (analyticsManager && analyticsManager.logClickInWebDemoTag) {
        analyticsManager.logClickInWebDemoTag();
      }
    };

    const handleWebDemoScroll = () => {
      if (analyticsManager && analyticsManager.logScrollInWebDemoTag) {
        analyticsManager.logScrollInWebDemoTag();
      }
    };

    return (
      <div style={{display: 'flex', flexDirection: 'column', width: "100%", alignItems: 'center', alignContent: 'center', justifyContent: 'center', color: "rgb(45, 55, 72)" }}>
        <div style={{position: "relative", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", background: HP_SLIDE_BG_HERO, paddingBottom: 64, boxSizing: "border-box"}}>
        <div style={floatingIndicatorStyle}>
          <div style={{display: "flex", alignItems: "center", gap: 8}}>
            <div style={{width: 20, height: 20, borderRadius: "50%", backgroundColor: HP_HERO_TEAL, display: "flex", alignItems: "center", justifyContent: "center"}}>
              <TrendingUpIcon style={{fontSize: 12, color: "#ffffff"}} />
            </div>
            <span>{t("HomeFloatingConversionTarget")}</span>
          </div>
        </div>
        <div style={{position: "relative", width: "100%", padding: "14px 16px", boxSizing: "border-box", background: "transparent", zIndex: 2}}>
          <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", cursor: "pointer"}} onClick={()=>{analyticsManager.logClickLogoTag();navigate(`${langPrefix}`);}}>
            <img src="/images/logo_gradient_transparent.png" alt="UpReality" height={46} style={{display: "block", height: 46, width: "auto"}} />
          </div>
          <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: "wrap", marginTop: 24}}>
            <button type="button" className="topbar-btn topbar-btn-active" aria-current="page">{t("TopbarHome")}</button>
            <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{analyticsManager.logClickPricingTag();navigate("/Pricing");}}>{t("Pricing")}</button>
            <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{navigate(`${langPrefix}/about`);}}>{t("AboutUs")}</button>
            <div style={{width: 7, height: 7, borderRadius: "50%", backgroundColor: "#04d3bc", margin: "0 2px", flexShrink: 0}} />
            <button type="button" className="topbar-btn topbar-btn-ghost" onClick={()=>{analyticsManager.logOpenPopupTag(null, "home_ad_login", true);setShowLogin("login");setLoginTitle(t('Login'));}}>{t("Login")}</button>
            <button type="button" className="topbar-btn topbar-btn-primary" onClick={()=>{analyticsManager.logOpenPopupTag(null, "home_ad_create_account", true);setShowLogin("account");setLoginTitle(t('CreateAccount'));}}>{t("CreateAccount")}</button>
          </div>
        </div>
        <br/>
        <div style={{position: "relative", zIndex: 2, width: "100%", paddingLeft: 16, paddingRight: 16, boxSizing: "border-box", textAlign: "center"}}>
          {renderHpHeroTitle(t, { compact: true, as: "h2" })}
        </div>
        <div style={{position: "relative", zIndex: 2, fontSize: 16, paddingLeft: 16, paddingRight: 16, marginTop: 16, marginBottom: 20, color: "#687b7d", lineHeight: 1.55, textAlign: "center"}}>{t("InstantAdHero41")}{t("InstantAdHero42")}</div>
        <div style={{position: "relative", zIndex: 2, marginTop: 20}}>
          <div style={getHpPhoneFrameShellStyle(274, 576)}>
          <div style={getHpPhonePlayerInsetStyle(14, 15)}>    
            <Player fromMaker={false} fromDemo={"top"} mini={true} storyId={"cafeuprealityad"} windowDimensions={getHpPhonePlayerBleedSize(244, 540)} /> 
          </div>
          {renderHpPhoneFrameOverlay({ frameWidth: 274, deviceBgHeight: 576, frameAlt: t("PhoneFrameAlt"), id: "hp-phonebg-top" })}
          </div>
        </div>                    
        <img src={"/images/from_ig_ig_profile_mobile_AD.webp"} alt={t('FromInstagramProfileAlt')} width="300" style={{position: "relative", zIndex: 2, marginTop: 10, marginRight: 18}}/>                           
        <div style={{marginTop: 50}}/>          
        <div style={{position: "relative", zIndex: 2, display: "flex", flexDirection: "row", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: 12, padding: "0 16px", boxSizing: "border-box"}}>
        <MaterialButton style="filled-brand-gradient" onClick={() => openCalendlyDemo(analyticsManager, "ad_scheduleDemo")} value={t("CTA")} />
        <MaterialButton style="filled" sx={HP_LANDING_CTA_SX} onClick={()=>{analyticsManager.logHPTryMakerTag("top_ad");navigate("/maker");}}  value={t("InstantAdCTA")} />
        </div>
        {renderHpHeroWaves({ height: 88 })}
        </div>
        <div style={{ marginBottom: 32 }} />
        <div className="scroll-slide-up" style={instantAdSlideShellStyle(INSTANT_AD_BG_WHITE, { compact: true })}>
        <StructuredData id="compare-instant-ad" data={{
          "@context": "https://schema.org",
          "@type": "Table",
          "about": { "@type": "Thing", "name": t('CompareWebsiteRestaurants') },
          "abstract": `${t('WebsiteQ1')}, ${t('WebsiteQ2')}, ${t('WebsiteQ3')} | ${t('StoryQ1')}, ${t('StoryQ2')}, ${t('StoryQ3')}`
        }} />
        <h2 style={instantAdSlideTitleStyleCompact}>{t('CompareWebsiteRestaurants')}</h2> 
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 20}}>
          <div style={getHpPhoneFrameShellStyle(274, 576)}>
            <div style={getHpPhonePlayerInsetStyle(14, 15)}>                            
            <WebPageMock 
              id="web_demo" 
              open={true} 
              url={`/demoWeb2/index.html?lang=${detectBrowserLanguage()}`} 
              width={getHpPhonePlayerBleedSize(244, 540).width}
              height={getHpPhonePlayerBleedSize(244, 540).height}
              imageOverWebsite={false} 
              close={()=>{}}
              onClick={handleWebDemoClick}
              onScroll={handleWebDemoScroll}
            />
            </div>
            {renderHpPhoneFrameOverlay({ frameWidth: 274, deviceBgHeight: 576 })}
          </div>
          <div style={{marginTop: 8, fontSize: 14, fontStyle: 'italic', color: '#8a94a6', textAlign: 'center'}}>{t('CompareFrameTraditionalWebsite')}</div>
        </div>
        <div style={{marginTop: 10}}>
          <ul>
            <li>{t('WebsiteQ1')}</li>
            <li>{t('WebsiteQ2')}</li>
            <li>{t('WebsiteQ3')}</li>
          </ul>
        </div>    
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 40}}>
          <div style={getHpPhoneFrameShellStyle(274, 540)}>
            <div style={getHpPhonePlayerInsetStyle(14, 14)}>    
              <Player fromMaker={false} fromDemo={"bottom"} mini={true} storyId={"cafeupreality"} windowDimensions={getHpPhonePlayerBleedSize(244, 508)} /> 
            </div>
            {renderHpPhoneFrameOverlay({ frameWidth: 274, deviceBgHeight: 540, id: "hp-phonebg-bottom" })}
          </div>
          <div style={{marginTop: 8, fontSize: 14, fontStyle: 'italic', color: '#8a94a6', textAlign: 'center'}}>{t('CompareFrameSocialWebsite')}</div>
        </div> 
        <div style={{marginTop: 10}}>
          <ul>
            <li>{t('StoryQ1')}</li>
            <li>{t('StoryQ2')}</li>
            <li>{t('StoryQ3')}</li>
          </ul>
        </div>
        <CompareWebsiteClosing mobile variant="restaurants" />
        </div>
        <div
          className="scroll-slide-up hp-planche-slide"
          style={{
            position: "relative",
            marginTop: 0,
            width: "100%",
            backgroundColor: isPhone ? HP_SLIDE_BG_WHO_FOR : HP_SLIDE_BG_HERO,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflow: "clip",
            padding: "72px 16px 40px",
          }}
        >
          {!isPhone ? renderHpDiscoveryPanoramaWaves() : null}
          <div style={{ position: "relative", zIndex: 1, width: "100%" }}>
            {renderPlancheDiscoveryLayout({
              t,
              Player,
              deviceBgHeight: 576,
              storyTopMargin: 15,
              storyHeight: 540,
              compact: true,
            })}
          </div>
        </div>
        <div className="scroll-slide-up" style={instantAdSlideShellStyle(INSTANT_AD_BG_WHITE, { compact: true })}>
            <h2 style={instantAdSlideTitleStyleCompact}>{t("InstantAdBenefit2")}</h2>
            {renderInstantAdCreateStepsList(t, { analyticsManager, setShowCreate1Clic })}
        </div>
        {renderWhoForMockupSlide(t, { MaterialButton, analyticsManager, navigate, compact: true, backgroundColor: HP_SLIDE_BG_WHO_FOR })}
        <div className="scroll-slide-up" style={{ marginTop: 0, width: "100%", backgroundColor: INSTANT_AD_BG_WHITE }}>
        <TestimonialsSection badgeKey="KFontaineTestimonialBadge" compact />
        </div>
        <div className="scroll-slide-up" style={instantAdSlideShellStyle(HP_SLIDE_BG_WHO_FOR, { compact: true })}>
          <StructuredData id="faq-instant-ad" data={MainHPConfig.faqSchema(t)} />
          <div className="hp-faq-on-grey">
            <Faq
              data={MainHPConfig.faqData(t)}
              styles={styles}
              config={{}}
            />
          </div>
          <div style={{ marginTop: 32, display: 'flex', flexDirection: 'row', justifyContent: 'center', width: "100%" }}>
            <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
              <MaterialButton style="filled-brand-gradient" onClick={() => openCalendlyDemo(analyticsManager, "ad_scheduleDemoFaq")} value={t("CTA")} />
              <div style={{marginTop: 20}}/>
              <MaterialButton style="filled" sx={HP_LANDING_CTA_SX} onClick={()=>{analyticsManager.logHPTryMakerTag("faq_ad");navigate("/maker");}}  value={t("InstantAdCTA")} />
            </div>
          </div>
        </div>
        <HpFooter analyticsManager={analyticsManager} compact />
      </div>
    );
  }
};

export {
  renderPlancheDiscoveryLayout,
  instantAdSlideShellStyle,
  instantAdSlideTitleStyle,
  getHpDesktopVisualMetrics,
  HP_DESKTOP_VISUAL_COMPACT_MAX_HEIGHT,
};